import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle, X, Plus, Trash2 } from 'lucide-react';

const ColumnMapper = ({ excelHeaders, entityFields, mappings, onMappingChange, autoMappings, sampleData }) => {
  const { t } = useTranslation();
  const [expandedSplits, setExpandedSplits] = useState({});

  const handleMappingChange = (excelColumn, fieldName) => {
    onMappingChange(excelColumn, fieldName);
  };

  const handleMultiFieldMapping = (excelColumn, fieldName, index = null) => {
    const currentMapping = mappings[excelColumn];
    
    // If this is a new multi-field mapping, initialize it
    if (!currentMapping || !currentMapping.fields) {
      const newMapping = {
        fields: [{ fieldName, splitIndex: 0 }],
        splitType: 'space' // default
      };
      onMappingChange(excelColumn, null, newMapping);
      return;
    }

    // Update existing multi-field mapping
    if (index !== null) {
      // Update existing field
      const updatedFields = [...currentMapping.fields];
      updatedFields[index] = { ...updatedFields[index], fieldName };
      onMappingChange(excelColumn, null, { ...currentMapping, fields: updatedFields });
    } else {
      // Add new field
      const newFields = [...currentMapping.fields, { fieldName, splitIndex: currentMapping.fields.length }];
      onMappingChange(excelColumn, null, { ...currentMapping, fields: newFields });
    }
  };

  const handleRemoveField = (excelColumn, index) => {
    const currentMapping = mappings[excelColumn];
    if (!currentMapping || !currentMapping.fields) return;
    
    const updatedFields = currentMapping.fields.filter((_, i) => i !== index);
    if (updatedFields.length === 0) {
      // Remove multi-field mapping, go back to single field
      onMappingChange(excelColumn, '');
    } else {
      onMappingChange(excelColumn, null, { ...currentMapping, fields: updatedFields });
    }
  };

  const handleSplitTypeChange = (excelColumn, splitType) => {
    const currentMapping = mappings[excelColumn];
    if (!currentMapping || !currentMapping.fields) return;
    
    onMappingChange(excelColumn, null, { ...currentMapping, splitType });
  };

  const handleSeparatorChange = (excelColumn, separator) => {
    const currentMapping = mappings[excelColumn];
    if (!currentMapping || !currentMapping.fields) return;
    
    onMappingChange(excelColumn, null, { ...currentMapping, separator });
  };

  const toggleSplitMode = (excelColumn) => {
    const currentMapping = mappings[excelColumn];
    
    if (currentMapping && currentMapping.fields) {
      // Switch from multi-field to single field
      const firstField = currentMapping.fields[0]?.fieldName || '';
      onMappingChange(excelColumn, firstField);
    } else {
      // Switch from single field to multi-field
      const fieldName = currentMapping?.fieldName || '';
      if (fieldName) {
        const newMapping = {
          fields: [{ fieldName, splitIndex: 0 }],
          splitType: 'space'
        };
        onMappingChange(excelColumn, null, newMapping);
      }
    }
  };

  const getFieldDisplayName = (fieldName) => {
    const field = entityFields.find(f => f.fieldName === fieldName);
    return field ? field.displayName : fieldName;
  };

  const isAutoMapped = (excelColumn) => {
    return autoMappings && autoMappings[excelColumn];
  };

  const getAutoMappingConfidence = (excelColumn) => {
    if (!autoMappings || !autoMappings[excelColumn]) return null;
    return autoMappings[excelColumn].confidence;
  };

  // Group fields by their database table and sort
  const groupedFields = () => {
    const groups = {
      Customer: [],
      Person: [],
      Company: [],
      Other: []
    };

    entityFields.forEach(field => {
      const table = field.database?.table || 'Other';
      if (table === 'Customers') {
        groups.Customer.push(field);
      } else if (table === 'Persons') {
        groups.Person.push(field);
      } else if (table === 'Companies') {
        groups.Company.push(field);
      } else {
        groups.Other.push(field);
      }
    });

    // Sort each group by displayName
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => a.displayName.localeCompare(b.displayName));
    });

    return groups;
  };

  const fieldGroups = groupedFields();

  // Get color class for field group
  const getGroupColorClass = (groupName) => {
    switch (groupName) {
      case 'Customer': return 'text-blue-700 dark:text-blue-300';
      case 'Person': return 'text-green-700 dark:text-green-300';
      case 'Company': return 'text-purple-700 dark:text-purple-300';
      default: return 'text-gray-700 dark:text-gray-300';
    }
  };

  // Get background color for optgroup
  const getGroupBgClass = (groupName) => {
    switch (groupName) {
      case 'Customer': return 'bg-blue-50 dark:bg-blue-900/20';
      case 'Person': return 'bg-green-50 dark:bg-green-900/20';
      case 'Company': return 'bg-purple-50 dark:bg-purple-900/20';
      default: return 'bg-gray-50 dark:bg-gray-900/20';
    }
  };

  return (
    <div className="space-y-4">
      {/* CSS for styling select optgroup and options */}
      <style>{`
        .field-mapper-select optgroup {
          font-weight: 600;
          font-size: 0.875rem;
          padding: 8px 0;
        }
        .field-mapper-select optgroup[label*="Customer"] {
          background-color: #dbeafe;
        }
        .field-mapper-select optgroup[label*="Person"] {
          background-color: #dcfce7;
        }
        .field-mapper-select optgroup[label*="Company"] {
          background-color: #f3e8ff;
        }
        .dark .field-mapper-select optgroup[label*="Customer"] {
          background-color: #1e3a8a;
        }
        .dark .field-mapper-select optgroup[label*="Person"] {
          background-color: #14532d;
        }
        .dark .field-mapper-select optgroup[label*="Company"] {
          background-color: #581c87;
        }
        .field-mapper-select option {
          padding: 6px 12px;
        }
      `}</style>
      
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
          {t('excelImport.mapColumns', 'Map each Excel column to an entity field. Required fields are marked with *.')}
        </p>
        
        {/* Field Group Legend */}
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center space-x-2">
            <span className="font-semibold text-blue-800 dark:text-blue-200">Field Groups:</span>
          </div>
          <div className="flex items-center space-x-1.5">
            <span className="inline-block w-3 h-3 rounded-full bg-blue-500"></span>
            <span className="text-blue-700 dark:text-blue-300 font-medium">📋 Customer</span>
          </div>
          <div className="flex items-center space-x-1.5">
            <span className="inline-block w-3 h-3 rounded-full bg-green-500"></span>
            <span className="text-green-700 dark:text-green-300 font-medium">👤 Person</span>
          </div>
          <div className="flex items-center space-x-1.5">
            <span className="inline-block w-3 h-3 rounded-full bg-purple-500"></span>
            <span className="text-purple-700 dark:text-purple-300 font-medium">🏢 Company</span>
          </div>
        </div>
      </div>

      <div className="space-y-3 max-h-[500px] overflow-y-auto">
        {excelHeaders.map((header) => {
          const mapping = mappings[header.name];
          const isMultiField = mapping && mapping.fields && Array.isArray(mapping.fields);
          const currentMapping = isMultiField ? null : (mapping?.fieldName || '');
          const autoMapped = isAutoMapped(header.name);
          const confidence = getAutoMappingConfidence(header.name);
          const field = isMultiField ? null : entityFields.find(f => f.fieldName === currentMapping);
          const isEmpty = header.isEmpty;

          // Get sample data for this column (data is keyed by column name)
          const sampleValue = sampleData && sampleData.data ? sampleData.data[header.name] : null;
          const displaySampleValue = sampleValue !== null && sampleValue !== undefined && sampleValue !== '' 
            ? String(sampleValue).substring(0, 50) + (String(sampleValue).length > 50 ? '...' : '')
            : null;

          // Check if this column is mapped
          const isMapped = isMultiField ? (mapping.fields && mapping.fields.length > 0) : (currentMapping && currentMapping !== '');
          
          return (
            <div key={header.index} className="space-y-2">
              <div
                className={`flex items-center space-x-4 p-3 rounded-lg border ${
                  isEmpty 
                    ? 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 opacity-60' 
                    : isMapped
                      ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                }`}
              >
              <div className="flex-1 min-w-0">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {header.originalName || header.name}
                  {isEmpty && (
                    <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                      ({t('excelImport.emptyColumn', 'Empty')})
                    </span>
                  )}
                  {autoMapped && !isEmpty && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      {t('excelImport.autoDetected', 'Auto-detected')}
                      {confidence && ` (${Math.round(confidence * 100)}%)`}
                    </span>
                  )}
                </label>
                {displaySampleValue && !isEmpty && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
                    {t('excelImport.exampleData', 'Example')}: <span className="font-mono">{displaySampleValue}</span>
                  </p>
                )}
              </div>

              <div className="flex-1">
                {!isMultiField ? (
                  <>
                    <select
                      value={currentMapping}
                      onChange={(e) => handleMappingChange(header.name, e.target.value)}
                      disabled={isEmpty}
                      className={`field-mapper-select w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm ${
                        isEmpty ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <option value="">{t('excelImport.skipColumn', '-- Skip Column --')}</option>
                      
                      {/* Customer Fields */}
                      {fieldGroups.Customer.length > 0 && (
                        <optgroup label="📋 Customer Fields" className="font-semibold">
                          {fieldGroups.Customer.map((field) => (
                            <option 
                              key={field.fieldName} 
                              value={field.fieldName}
                              className="text-blue-700 dark:text-blue-300"
                            >
                              {field.displayName}
                              {field.required && !field.autoGenerate && ' *'}
                              {field.autoGenerate?.enabled && ' (Auto)'}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      
                      {/* Person Fields */}
                      {fieldGroups.Person.length > 0 && (
                        <optgroup label="👤 Person Fields" className="font-semibold">
                          {fieldGroups.Person.map((field) => (
                            <option 
                              key={field.fieldName} 
                              value={field.fieldName}
                              className="text-green-700 dark:text-green-300"
                            >
                              {field.displayName}
                              {field.required && !field.autoGenerate && ' *'}
                              {field.autoGenerate?.enabled && ' (Auto)'}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      
                      {/* Company Fields */}
                      {fieldGroups.Company.length > 0 && (
                        <optgroup label="🏢 Company Fields" className="font-semibold">
                          {fieldGroups.Company.map((field) => (
                            <option 
                              key={field.fieldName} 
                              value={field.fieldName}
                              className="text-purple-700 dark:text-purple-300"
                            >
                              {field.displayName}
                              {field.required && !field.autoGenerate && ' *'}
                              {field.autoGenerate?.enabled && ' (Auto)'}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      
                      {/* Other Fields */}
                      {fieldGroups.Other.length > 0 && (
                        <optgroup label="⚙️ Other Fields" className="font-semibold">
                          {fieldGroups.Other.map((field) => (
                            <option 
                              key={field.fieldName} 
                              value={field.fieldName}
                              className="text-gray-700 dark:text-gray-300"
                            >
                              {field.displayName}
                              {field.required && !field.autoGenerate && ' *'}
                              {field.autoGenerate?.enabled && ' (Auto)'}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                    {isMapped && !isEmpty && (
                      <button
                        type="button"
                        onClick={() => toggleSplitMode(header.name)}
                        className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
                      >
                        {t('excelImport.splitToMultipleFields', '⚡ Split to multiple fields')}
                      </button>
                    )}
                  </>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        {t('excelImport.multiFieldMapping', 'Multi-Field Mapping')}
                      </span>
                      <button
                        type="button"
                        onClick={() => toggleSplitMode(header.name)}
                        className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                      >
                        {t('excelImport.switchToSingle', 'Switch to single field')}
                      </button>
                    </div>
                    {mapping.fields.map((fieldMapping, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <select
                          value={fieldMapping.fieldName || ''}
                          onChange={(e) => handleMultiFieldMapping(header.name, e.target.value, idx)}
                          disabled={isEmpty}
                          className={`field-mapper-select flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm ${
                            isEmpty ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <option value="">{t('excelImport.selectField', 'Select Field')}</option>
                          {Object.entries(fieldGroups).map(([groupName, fields]) => (
                            fields.length > 0 && (
                              <optgroup key={groupName} label={`${groupName} Fields`}>
                                {fields.map((field) => (
                                  <option key={field.fieldName} value={field.fieldName}>
                                    {field.displayName}
                                    {field.required && !field.autoGenerate && ' *'}
                                  </option>
                                ))}
                              </optgroup>
                            )
                          ))}
                        </select>
                        {mapping.fields.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveField(header.name, idx)}
                            className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                            title={t('excelImport.removeField', 'Remove field')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => handleMultiFieldMapping(header.name, '', null)}
                      disabled={isEmpty}
                      className="flex items-center space-x-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-50"
                    >
                      <Plus className="h-3 w-3" />
                      <span>{t('excelImport.addField', 'Add another field')}</span>
                    </button>
                  </div>
                )}
              </div>

              {field && (
                <div className="flex-shrink-0">
                  {field.required && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                      {t('excelImport.required', 'Required')}
                    </span>
                  )}
                  {field.lookup?.enabled && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 ml-2">
                      🔍 {t('excelImport.lookup', 'Lookup')}
                    </span>
                  )}
                </div>
              )}
              </div>
              
              {/* Multi-Field Split Configuration */}
              {isMultiField && mapping.fields && mapping.fields.length > 0 && (
                <div className="mt-2 ml-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-blue-800 dark:text-blue-200">
                      {t('excelImport.splitConfiguration', 'Split Configuration')}
                    </p>
                    <button
                      type="button"
                      onClick={() => setExpandedSplits(prev => ({ ...prev, [header.name]: !prev[header.name] }))}
                      className="text-xs text-blue-600 dark:text-blue-400"
                    >
                      {expandedSplits[header.name] ? '▼' : '▶'}
                    </button>
                  </div>
                  {expandedSplits[header.name] && (
                    <div className="space-y-2 mt-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('excelImport.splitType', 'Split Type')}
                        </label>
                        <select
                          value={mapping.splitType || 'space'}
                          onChange={(e) => handleSplitTypeChange(header.name, e.target.value)}
                          className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="space">{t('excelImport.splitBySpace', 'Split by Space')} (e.g., "John Doe" → ["John", "Doe"])</option>
                          <option value="parentheses">{t('excelImport.splitByParentheses', 'Split by Parentheses')} (e.g., "Company (Branch)" → ["Company", "Branch"])</option>
                          <option value="custom">{t('excelImport.splitByCustom', 'Custom Separator')}</option>
                        </select>
                      </div>
                      {mapping.splitType === 'custom' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t('excelImport.customSeparator', 'Custom Separator')}
                          </label>
                          <input
                            type="text"
                            value={mapping.separator || ''}
                            onChange={(e) => handleSeparatorChange(header.name, e.target.value)}
                            placeholder={t('excelImport.separatorPlaceholder', 'e.g., ",", "-", "|"')}
                            className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                      )}
                      {displaySampleValue && (
                        <div className="mt-2 p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                            {t('excelImport.preview', 'Preview')}:
                          </p>
                          <div className="text-xs font-mono text-gray-800 dark:text-gray-200">
                            {(() => {
                              const value = String(displaySampleValue);
                              let parts = [];
                              if (mapping.splitType === 'space') {
                                parts = value.split(/\s+/);
                              } else if (mapping.splitType === 'parentheses') {
                                const match = value.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
                                if (match) {
                                  parts = [match[1].trim(), match[2].trim()];
                                } else {
                                  parts = [value];
                                }
                              } else if (mapping.splitType === 'custom' && mapping.separator) {
                                parts = value.split(mapping.separator);
                              } else {
                                parts = [value];
                              }
                              return parts.map((part, i) => (
                                <span key={i} className="inline-block mr-2 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">
                                  {part.trim() || `[${i}]`}
                                </span>
                              ));
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Show lookup info if field has lookup enabled */}
              {field && field.lookup?.enabled && (
                <div className="mt-2 ml-4 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                  <p className="text-xs font-semibold text-purple-800 dark:text-purple-200 mb-2">
                    📋 {t('excelImport.acceptedFormats', 'Accepted Formats for')} {field.displayName}:
                  </p>
                  <ul className="text-xs text-purple-700 dark:text-purple-300 space-y-1">
                    {field.lookup.acceptedFormats && field.lookup.acceptedFormats.length > 0 ? (
                      field.lookup.acceptedFormats.map((format, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>{format}</span>
                        </li>
                      ))
                    ) : (
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>{t('excelImport.lookupGeneric', 'Enter name, code, or identifier')}</span>
                      </li>
                    )}
                  </ul>
                  <p className="text-xs text-purple-600 dark:text-purple-400 mt-2 italic">
                    💡 {t('excelImport.lookupNote', 'System will automatically find matching records')}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {Object.keys(mappings).filter(key => mappings[key]?.fieldName).length === 0 && (
        <div className="flex items-center p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <AlertCircle className="h-5 w-5 text-yellow-500 mr-2 flex-shrink-0" />
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            {t('excelImport.noMappings', 'No columns mapped. Please map at least one column to continue.')}
          </p>
        </div>
      )}
    </div>
  );
};

export default ColumnMapper;

